import { useTranslation } from 'react-i18next';

import Badge from '@Components/Badge/Badge';
import { FirmStoreSummary, Schedule } from '@Models';
import { getRetailerName, UPPERCASE_LABEL_CLASS_NAME } from '../../FieldServices/constants';
import getScheduleStatusVariant from '../constants';
import { formatScheduleRecurrence } from '../Wizard/constants';
import { getAlertCeilingDisplay, getScheduleComposition } from './constants';

interface ScheduleSummaryFactsProps {
  schedule: Schedule;
  storesCount: number;
  retailers: string[];
  firms: FirmStoreSummary[];
}

const ScheduleSummaryFacts = ({ schedule, storesCount, retailers, firms }: ScheduleSummaryFactsProps) => {
  const { t, i18n } = useTranslation();
  const composition = getScheduleComposition(schedule.alert_ceiling_pct);
  const alertCeiling = getAlertCeilingDisplay(schedule.alert_ceiling_pct);

  return (
    <div className="flex flex-col gap-3 px-[18px] py-3.5">
      <div className="flex items-center justify-between gap-2">
        <span className={UPPERCASE_LABEL_CLASS_NAME}>{t('Schedule.Detail.Sidebar.ScheduleLabel')}</span>
        <Badge
          variant={getScheduleStatusVariant(schedule.status)}
          label={t(`Schedule.Status.${schedule.status}`)}
          showDot
          size="sm"
          className="flex-shrink-0 rounded-full"
        />
      </div>
      <div className="truncate text-sm font-bold text-gray-900">{schedule.name}</div>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-xs text-gray-500">{t('Schedule.Detail.Metadata.Cadence')}</span>
          <span className="text-right text-xs font-semibold text-gray-900">
            {formatScheduleRecurrence(schedule, t, i18n.language)}
          </span>
        </div>
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-xs text-gray-500">{t('Schedule.Detail.Sidebar.Composition')}</span>
          <span className="text-xs font-semibold text-gray-900">
            {t(`Schedule.Detail.Sidebar.CompositionValue.${composition}`)}
          </span>
        </div>
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-xs text-gray-500">{t('Schedule.Detail.Metadata.Stores')}</span>
          <span className="text-xs font-semibold tabular-nums text-gray-900">
            {storesCount.toLocaleString(i18n.language)}
          </span>
        </div>
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-xs text-gray-500">{t('Schedule.Detail.Metadata.Retailer')}</span>
          <span className="text-xs font-semibold text-gray-900">
            {retailers.length > 0 ? retailers.map(getRetailerName).join(', ') : t('Schedule.Detail.Metadata.None')}
          </span>
        </div>
        <div className="flex items-baseline justify-between gap-2">
          <span className="flex-shrink-0 text-xs text-gray-500">{t('Schedule.Detail.Metadata.FieldServices')}</span>
          {firms.length > 0 ? (
            <ul className="flex flex-col items-end text-right text-xs font-semibold text-gray-900">
              {firms.map((firm) => (
                <li key={firm.name}>
                  {t('Schedule.Detail.Metadata.FirmWithStoreCount', {
                    name: firm.name,
                    storeCount: t('Schedule.Card.StoresCount', { count: firm.store_count }),
                  })}
                </li>
              ))}
            </ul>
          ) : (
            <span className="text-xs font-semibold text-gray-900">{t('Schedule.Detail.Metadata.None')}</span>
          )}
        </div>
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-xs text-gray-500">{t('Schedule.Detail.Sidebar.AlertCeiling')}</span>
          <span className="text-xs font-semibold text-gray-900">
            {alertCeiling === null
              ? t('Schedule.Detail.Sidebar.AlertCeilingDoesNotApply')
              : t('Schedule.Detail.Sidebar.AlertCeilingPercent', { value: alertCeiling })}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ScheduleSummaryFacts;
